import React, { useState, useEffect, useRef } from 'react';
import { LogIn, User, X, ChevronDown } from 'lucide-react';

const AssessmentForm = ({ 
  authToken, 
  notes, 
  setNotes, 
  onAnalyze, 
  onLogin, 
  currentCase = null,
  cases = [],
  onSelectCase,
  onClearCase
}) => {
  const [showCaseDropdown, setShowCaseDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowPatientDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  if (!authToken) {
    return (
      <div className="h-[calc(100vh-4rem)] bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center px-6 overflow-y-auto">
        <div className="max-w-md w-full bg-white border-2 border-gray-200 rounded-xl shadow-lg p-8 text-center">
          <div className="mb-6">
            <div className="bg-teal-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <LogIn className="text-teal-700" size={32} />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Authentication Required</h3>
            <p className="text-gray-600">
              You need to be logged in to generate FIM/FAM assessments.
            </p>
          </div>
          <button
            onClick={onLogin}
            className="bg-teal-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors inline-flex items-center gap-2 shadow-md hover:shadow-lg"
          >
            <LogIn size={20} />
            Log In to Continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-4rem)] bg-gradient-to-br from-teal-50 to-blue-50 overflow-y-auto">
      <div className="max-w-4xl mx-auto px-6 py-4">
        {/* Header */}
        <div className="text-center mb-3">
          <h2 className="text-3xl font-bold text-gray-900 mb-1">FIM/FAM Assessment</h2>
          <p className="text-sm text-gray-600">AI-powered clinical assessment generation</p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-lg p-5 border-2 border-gray-200">
          {/* Case Selector */}
          <div className="mb-3">
            {!currentCase ? (
              <div ref={dropdownRef} className="relative">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Case (Optional)
                </label>
                <button
                  onClick={() => setShowCaseDropdown(!showCaseDropdown)}
                  className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-lg hover:border-teal-400 focus:border-teal-500 focus:outline-none transition-colors flex items-center justify-between text-gray-600 hover:text-gray-900"
                >
                  <div className="flex items-center gap-2">
                    <User size={18} className="text-gray-400" />
                    <span className="text-sm font-medium">Select a case</span>
                  </div>
                  <ChevronDown size={18} className={`transition-transform ${showCaseDropdown ? 'rotate-180' : ''}`} />
                </button>
                
                {showCaseDropdown && cases.length > 0 && (
                  <div className="absolute z-10 w-full mt-2 bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {cases.map(c => (
                      <button
                        key={c.caseId}
                        onClick={() => {
                          onSelectCase && onSelectCase(c.caseId);
                          setShowCaseDropdown(false);
                        }}
                        className="w-full px-4 py-3 text-left hover:bg-teal-50 transition-colors flex items-center gap-3 border-b border-gray-100 last:border-b-0"
                      >
                        <User size={16} className="text-teal-600" />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{c.name}</div>
                          {c.assessmentCount > 0 && (
                            <div className="text-xs text-gray-500">{c.assessmentCount} assessment{c.assessmentCount !== 1 ? 's' : ''}</div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                
                {showCaseDropdown && cases.length === 0 && (
                  <div className="absolute z-10 w-full mt-2 bg-white border-2 border-gray-200 rounded-lg shadow-lg p-4 text-center text-sm text-gray-500">
                    Visit "My Cases" to add your first case.
                  </div>
                )}
              </div>
            ) : (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Case
                </label>
                <div className="inline-flex items-center gap-2 bg-teal-50 border-2 border-teal-200 px-4 py-3 rounded-lg shadow-sm">
                  <User className="text-teal-600" size={20} />
                  <span className="text-teal-800 font-semibold">{currentCase.name}</span>
                  <button 
                    onClick={onClearCase}
                    className="ml-2 p-1 text-teal-700 hover:text-teal-900 hover:bg-teal-100 rounded transition-colors"
                    title="Clear case selection"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Clinical Notes */}
          <div className="mb-3">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Clinical Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Paste or type clinical notes here..."
              className="w-full h-[calc(100vh-36rem)] min-h-[200px] p-4 border-2 border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none resize-none text-gray-800 placeholder-gray-400"
            />
          </div>

          {/* Analyze Button */}
          <button
            onClick={onAnalyze}
            disabled={!notes.trim()}
            className={`w-full py-3 rounded-lg font-semibold text-lg transition-all shadow-md ${
              notes.trim()
                ? 'bg-teal-600 text-white hover:bg-teal-700 hover:shadow-lg'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Analyze Notes
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssessmentForm;

